/**
 * Shared text-on-image styling tokens.
 *
 * The Daily page's `<ReleaseCard>` and the Days page's `<DayCard>`
 * (and any future card that puts type over photographic backgrounds)
 * share the same legibility need: a strong drop-shadow + opaque
 * white foreground that reads cleanly against any artwork. Hand-
 * rolling those `sx` blocks per call-site drifts over time; this
 * module is the single source.
 *
 * The values are exposed as `SxProps`-shaped fragments rather than
 * as a typography variant module-augmentation so they compose with
 * MUI's existing `variant` prop (`<Typography variant="h3"
 * sx={textOnImageHeading}>`). This keeps the SPA's typography scale
 * in one place (the branding-derived theme) and the on-image
 * styling in another, instead of forking the scale into separate
 * `h3OnImage` / `body1OnImage` variants.
 */

import type { SxProps, Theme } from '@mui/material/styles';

/**
 * Shared text shadow for foreground text on photographic
 * backgrounds. Two-layer drop-shadow with a tight black halo on
 * top of a softer wider blur so type stays sharp even on bright /
 * busy artwork.
 */
export const HIGH_CONTRAST_TEXT_SHADOW =
  '0 1px 2px rgba(0,0,0,0.85), 0 2px 6px rgba(0,0,0,0.55)';

/** Headings displayed over a photographic background. */
export const textOnImageHeading: SxProps<Theme> = {
  color: 'common.white',
  textShadow: HIGH_CONTRAST_TEXT_SHADOW,
  lineHeight: 1.15,
  fontWeight: 700,
};

/** Body / subtitle copy displayed over a photographic background. */
export const textOnImageBody: SxProps<Theme> = {
  color: 'common.white',
  textShadow: HIGH_CONTRAST_TEXT_SHADOW,
  lineHeight: 1.4,
  opacity: 0.96,
};
